import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  cleanDateForApi,
  formatPhoneAsYouType,
  getMaxDateForInput,
  parseInternationalPhone,
  type SupportedPhoneCountry,
} from '@digitaltattoo/shared-utils';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Shield, 
  CheckCircle, 
  AlertTriangle, 
  Calendar,
  FileText,
  Upload,
  Eye,
  Trash2,
  QrCode,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  Info,
  Loader2
} from 'lucide-react';
import { ClientCreationFormData as BaseClientCreationFormData, ClientCreationResponse } from '@digitaltattoo/types';
import { InternationalPhoneInput, type InternationalPhoneChangeResult } from './InternationalPhoneInput';

// Extended type with file upload fields
interface ClientCreationFormData extends Omit<BaseClientCreationFormData, 'identityDocument' | 'legalGuardian'> {
  identityDocument: BaseClientCreationFormData['identityDocument'] & {
    fileUrl?: string;
    fileName?: string;
  };
  legalGuardian?: Omit<NonNullable<BaseClientCreationFormData['legalGuardian']>, 'identityDocument'> & {
    identityDocument: NonNullable<BaseClientCreationFormData['legalGuardian']>['identityDocument'] & {
      fileUrl?: string;
      fileName?: string;
    };
  };
}

interface ClientCreationFormProps {
  onSuccess?: (response: ClientCreationResponse) => void;
  onCancel?: () => void;
  isBackoffice?: boolean;
  requireLegalGuardian?: boolean; // Project parameter flag
  preferredLanguage?: 'fr' | 'en'; // Preferred language
  createUserAccount?: boolean; // Whether to create user account (default false for guest)
  initialValues?: Partial<ClientCreationFormData>;
}

const getPhoneState = (
  value: string,
  fallbackCountry: SupportedPhoneCountry = 'FR',
): InternationalPhoneChangeResult => {
  const parsed = parseInternationalPhone(value, fallbackCountry);
  const country = parsed.country ?? fallbackCountry;
  return {
    displayValue: formatPhoneAsYouType(value, country),
    e164Value: parsed.isValid ? parsed.e164 : null,
    country,
    isValid: parsed.isValid,
  };
};

export function ClientCreationForm({ 
  onSuccess, 
  onCancel, 
  isBackoffice = false,
  requireLegalGuardian = false,
  preferredLanguage = 'fr',
  createUserAccount = false,
  initialValues
}: ClientCreationFormProps) {
  const { t } = useTranslation();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<ClientCreationFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    isMinor: null,
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'France'
    },
    identityDocument: {
      type: 'national_id',
      number: '',
      issueDate: '',
      expiryDate: '',
      issuePlace: '',
      issuingAuthority: ''
    },
    legalGuardian: undefined,
    consents: {
      dataProcessing: false,
      photography: false,
      marketing: false,
      minorConsent: false,
      guardianPresence: false
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState<ClientCreationResponse | null>(null);
  const isMinor = formData.isMinor === true;
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadingGuardianDocument, setUploadingGuardianDocument] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [identityDocLaterAcknowledged, setIdentityDocLaterAcknowledged] = useState(false);
  const [phoneState, setPhoneState] = useState<InternationalPhoneChangeResult>(() => getPhoneState(''));
  const [guardianPhoneState, setGuardianPhoneState] = useState<InternationalPhoneChangeResult>(() => getPhoneState(''));
  
  // Refs for debouncing
  const emailCheckTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const phoneCheckTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const steps = [
    { number: 1, title: t('clientForm.steps.basicInfo') },
    { number: 2, title: t('clientForm.steps.address') },
    { number: 3, title: t('clientForm.steps.identity') },
    { number: 4, title: t('clientForm.steps.guardian') },
    { number: 5, title: t('clientForm.steps.consents') }
  ];

  useEffect(() => {
    if (!initialValues) return;
    const nextPhoneState = initialValues.phone !== undefined ? getPhoneState(initialValues.phone) : null;
    const nextGuardianPhoneState = initialValues.legalGuardian?.phone !== undefined
      ? getPhoneState(initialValues.legalGuardian.phone)
      : null;
    if (nextPhoneState) setPhoneState(nextPhoneState);
    if (nextGuardianPhoneState) setGuardianPhoneState(nextGuardianPhoneState);
    setFormData(prev => ({
      ...prev,
      firstName: initialValues.firstName ?? prev.firstName,
      lastName: initialValues.lastName ?? prev.lastName,
      email: initialValues.email ?? prev.email,
      phone: nextPhoneState?.displayValue ?? prev.phone,
      dateOfBirth: initialValues.dateOfBirth ?? prev.dateOfBirth,
      isMinor: initialValues.isMinor ?? prev.isMinor,
      legalGuardian: initialValues.legalGuardian
        ? { ...initialValues.legalGuardian, phone: nextGuardianPhoneState?.displayValue ?? initialValues.legalGuardian.phone }
        : prev.legalGuardian,
    }));
  }, [initialValues]);

  // Check if client is minor when date of birth changes
  useEffect(() => {
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      const clientIsMinor = age < 18;
      setFormData(prev => ({ ...prev, isMinor: clientIsMinor }));
      
      // Only require guardian if requireLegalGuardian flag is true AND client is minor
      if (requireLegalGuardian && clientIsMinor && !formData.legalGuardian) {
        // Initialize legal guardian data for minors
        setFormData(prev => ({
          ...prev,
          legalGuardian: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            relationshipToMinor: 'parent',
            relationshipDetails: '',
            identityDocument: {
              type: 'national_id',
              number: '',
              issueDate: '',
              expiryDate: '',
              issuePlace: '',
              issuingAuthority: ''
            }
          }
        }));
      } else if ((!requireLegalGuardian || !clientIsMinor) && formData.legalGuardian) {
        // Remove legal guardian data if not required
        setGuardianPhoneState(getPhoneState(''));
        setFormData(prev => ({
          ...prev,
          legalGuardian: undefined
        }));
      }
    }
  }, [formData.dateOfBirth, requireLegalGuardian]);

  // Check for duplicate email/phone
  const checkDuplicate = useCallback(async (field: 'email' | 'phone', value: string) => {
    if (!value || value.trim().length === 0) {
      return;
    }

    // Basic validation before checking for duplicates
    if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return; // Let the existing validation handle invalid format
      }
    } else if (field === 'phone') {
      const phoneDigits = value.replace(/\D/g, '');
      if (phoneDigits.length < 8) {
        return; // Let the existing validation handle invalid format
      }
    }

    try {
      const setter = field === 'email' ? setCheckingEmail : setCheckingPhone;
      setter(true);

      const response = await fetch('/api/clients/check-duplicate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [field]: value }),
      });

      const data = await response.json();

      if (data.success && data.exists) {
        setErrors(prev => ({
          ...prev,
          [field]: t('clientForm.validation.' + field + 'Exists')
        }));
      } else {
        // Clear the error if no duplicate found
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    } catch (error) {
      console.error(`Error checking duplicate ${field}:`, error);
    } finally {
      const setter = field === 'email' ? setCheckingEmail : setCheckingPhone;
      setter(false);
    }
  }, [t]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
      if (phoneCheckTimeoutRef.current) {
        clearTimeout(phoneCheckTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (field: string, value: any) => {
    const keys = field.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Trigger duplicate check with debouncing for email and phone
    if (field === 'email') {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
      emailCheckTimeoutRef.current = setTimeout(() => {
        checkDuplicate('email', value);
      }, 800); // 800ms debounce
    } else if (field === 'phone') {
      if (phoneCheckTimeoutRef.current) {
        clearTimeout(phoneCheckTimeoutRef.current);
      }
      phoneCheckTimeoutRef.current = setTimeout(() => {
        checkDuplicate('phone', value);
      }, 800); // 800ms debounce
    }
  };

  const handleDocumentUpload = async (file: File, isGuardianDocument = false) => {
    try {
      if (isGuardianDocument) {
        setUploadingGuardianDocument(true);
      } else {
        setUploadingDocument(true);
      }

      // Validate file
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error(t('clientForm.validation.invalidFileType'));
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB
        throw new Error(t('clientForm.validation.fileTooLarge'));
      }

      // Mock file upload - in real implementation, upload to storage service
      const mockFileUrl = `https://storage.digitaltattoo.fr/documents/temp/${Date.now()}-${file.name}`;
      
      // Update form data with file information
      if (isGuardianDocument) {
        handleInputChange('legalGuardian.identityDocument.fileUrl', mockFileUrl);
        handleInputChange('legalGuardian.identityDocument.fileName', file.name);
      } else {
        handleInputChange('identityDocument.fileUrl', mockFileUrl);
        handleInputChange('identityDocument.fileName', file.name);
      }

      console.log('Document uploaded:', { fileName: file.name, url: mockFileUrl, isGuardian: isGuardianDocument });
      
    } catch (error) {
      console.error('Document upload error:', error);
      setErrors(prev => ({
        ...prev,
        [isGuardianDocument ? 'guardianDocument' : 'clientDocument']: error instanceof Error ? error.message : 'Upload failed'
      }));
    } finally {
      if (isGuardianDocument) {
        setUploadingGuardianDocument(false);
      } else {
        setUploadingDocument(false);
      }
    }
  };

  const handleDocumentDelete = (isGuardianDocument = false) => {
    if (isGuardianDocument) {
      handleInputChange('legalGuardian.identityDocument.fileUrl', '');
      handleInputChange('legalGuardian.identityDocument.fileName', '');
    } else {
      handleInputChange('identityDocument.fileUrl', '');
      handleInputChange('identityDocument.fileName', '');
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1: // Basic Info
        if (!formData.firstName.trim()) {
          newErrors.firstName = t('clientForm.validation.firstNameRequired');
        }
        if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = t('clientForm.validation.emailInvalid');
        } else if (errors.email && errors.email.includes(t('clientForm.validation.emailExists'))) {
          // Preserve existing duplicate error
          newErrors.email = errors.email;
        }
        if (phoneState.displayValue.trim() && (!phoneState.isValid || !phoneState.e164Value)) {
          newErrors.phone = t('clientForm.validation.phoneInvalid');
        } else if (errors.phone && errors.phone.includes(t('clientForm.validation.phoneExists'))) {
          // Preserve existing duplicate error
          newErrors.phone = errors.phone;
        }
        break;

      case 2: // Address
        break;

      case 3: // Identity Document
        // Check if user has either uploaded a document OR acknowledged they'll provide it later
        const hasUploadedDocument = formData.identityDocument?.fileUrl && formData.identityDocument?.fileUrl.length > 0;
        const hasEnteredDocumentInfo = formData.identityDocument?.number && formData.identityDocument?.number.trim().length > 0;
        
        void hasUploadedDocument;
        void hasEnteredDocumentInfo;
        break;

      case 4: // Legal Guardian (only if requireLegalGuardian && isMinor)
        if (requireLegalGuardian && isMinor && formData.legalGuardian) {
          if (
            formData.legalGuardian.email.trim()
            && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.legalGuardian.email)
          ) {
            newErrors['legalGuardian.email'] = t('clientForm.validation.emailInvalid');
          }
          if (guardianPhoneState.displayValue.trim() && (!guardianPhoneState.isValid || !guardianPhoneState.e164Value)) {
            newErrors['legalGuardian.phone'] = t('clientForm.validation.phoneInvalid');
          }
        }
        break;

      case 5: // Consents
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      // Skip guardian step if not required (requireLegalGuardian && isMinor)
      if (currentStep === 3 && (!requireLegalGuardian || !isMinor)) {
        setCurrentStep(5);
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handlePrevious = () => {
    // Skip guardian step if not a minor
    if (currentStep === 5 && !isMinor) {
      setCurrentStep(3);
    } else {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) {
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      console.log('Submitting client creation with data:', {
        ...formData,
        identityDocument: formData.identityDocument ? {
          ...formData.identityDocument,
          number: formData.identityDocument.number ? '***' + formData.identityDocument.number.slice(-4) : 'none'
        } : null,
        legalGuardian: formData.legalGuardian ? {
          ...formData.legalGuardian,
          identityDocument: formData.legalGuardian.identityDocument ? {
            ...formData.legalGuardian.identityDocument,
            number: formData.legalGuardian.identityDocument.number ? '***' + formData.legalGuardian.identityDocument.number.slice(-4) : 'none'
          } : null
        } : null
      });

      // Clean form data before sending - handle date fields and empty values
      const cleanedFormData = {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: phoneState.e164Value ?? '',
        dateOfBirth: cleanDateForApi(formData.dateOfBirth),
        isMinor,
        preferredLanguage,
        requireLegalGuardian,
        identityDocument: formData.identityDocument ? {
          ...formData.identityDocument,
          issueDate: cleanDateForApi(formData.identityDocument.issueDate),
          expiryDate: cleanDateForApi(formData.identityDocument.expiryDate),
        } : undefined,
        legalGuardian: formData.legalGuardian ? {
          ...formData.legalGuardian,
          firstName: formData.legalGuardian.firstName.trim(),
          lastName: formData.legalGuardian.lastName.trim(),
          email: formData.legalGuardian.email.trim(),
          phone: guardianPhoneState.e164Value ?? '',
          identityDocument: formData.legalGuardian.identityDocument ? {
            ...formData.legalGuardian.identityDocument,
            issueDate: cleanDateForApi(formData.legalGuardian.identityDocument.issueDate),
            expiryDate: cleanDateForApi(formData.legalGuardian.identityDocument.expiryDate),
          } : undefined,
        } : undefined,
      };

      console.log('Sending cleaned form data:', {
        ...cleanedFormData,
        identityDocument: cleanedFormData.identityDocument ? {
          ...cleanedFormData.identityDocument,
          number: cleanedFormData.identityDocument.number ? '***' + cleanedFormData.identityDocument.number.slice(-4) : 'none'
        } : null,
        legalGuardian: cleanedFormData.legalGuardian ? {
          ...cleanedFormData.legalGuardian,
          identityDocument: cleanedFormData.legalGuardian.identityDocument ? {
            ...cleanedFormData.legalGuardian.identityDocument,
            number: cleanedFormData.legalGuardian.identityDocument.number ? '***' + cleanedFormData.legalGuardian.identityDocument.number.slice(-4) : 'none'
          } : null
        } : null
      });

      const response = await fetch('/api/clients/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanedFormData),
      });

      const data: ClientCreationResponse = await response.json();

      if (data.success) {
        setSuccessData(data);
        setShowSuccess(true);
        onSuccess?.(data);
      } else {
        if (data.errors) {
          const errorMap: Record<string, string> = {};
          data.errors.forEach(error => {
            errorMap[error.field] = error.message;
          });
          setErrors(errorMap);
        } else {
          const raw = data.message || '';
          const isDuplicate =
            raw === 'Client already exists with this email or phone number'
            || raw === 'Un client existe déjà avec cet e-mail ou ce numéro de téléphone.';
          setErrors({
            general: isDuplicate
              ? t('customerDetails.warning.bothExist')
              : (raw || t('customerDetails.guest.errors.createFailed')),
          });
        }
      }
    } catch (error) {
      console.error('Client creation error:', error);
      setErrors({ general: t('clientForm.error.networkError') });
    } finally {
      setLoading(false);
    }
  };

  const renderDocumentSection = (document: any, isGuardianDocument = false) => {
    const hasFile = document?.fileUrl && document?.fileName;
    const uploading = isGuardianDocument ? uploadingGuardianDocument : uploadingDocument;
    const errorKey = isGuardianDocument ? 'guardianDocument' : 'clientDocument';

    return (
      <div className="space-y-4">
        {/* Document File Management */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          {hasFile ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="font-medium text-gray-900">{document.fileName}</p>
                  <p className="text-sm text-gray-500">Document téléchargé</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => window.open(document.fileUrl, '_blank')}
                  className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Voir le document"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDocumentDelete(isGuardianDocument)}
                  className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  title="Supprimer le document"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  {isGuardianDocument ? 
                    t('clientForm.guardianDocumentUpload.description') : 
                    t('clientForm.documentUpload.description')
                  }
                </p>
                <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? t('clientForm.documentUpload.uploading') : t('clientForm.documentUpload.selectFile')}
                  <input
                    type="file"
                    className="sr-only"
                    accept=".jpg,.jpeg,.png,.pdf"
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleDocumentUpload(file, isGuardianDocument);
                      }
                    }}
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Replace Document Button */}
        {hasFile && (
          <div className="flex justify-center">
            <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Téléchargement...' : 'Remplacer le document'}
              <input
                type="file"
                className="sr-only"
                accept=".jpg,.jpeg,.png,.pdf"
                disabled={uploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleDocumentUpload(file, isGuardianDocument);
                  }
                }}
              />
            </label>
          </div>
        )}

        {/* Upload Error */}
        {errors[errorKey] && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 text-sm">{errors[errorKey]}</p>
          </div>
        )}
      </div>
    );
  };

  const renderProgressBar = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
      <div className="flex items-center justify-center space-x-2 mb-8">
        {steps.map((step, index, array) => (
          <React.Fragment key={step.number}>
            <div className={`flex flex-col items-center text-center flex-shrink-0 ${
              currentStep >= step.number ? 'text-primary-600' : 'text-gray-500'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= step.number ? 'bg-primary-300 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step.number}
              </div>
              <p className="mt-2 text-xs font-medium leading-tight">{step.title}</p>
            </div>
            {index < array.length - 1 && (
              <div className="flex-1 h-px bg-gray-300 mx-2"></div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-textPrimary mb-4">
          {t('clientForm.steps.basicInfo')}
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Informations personnelles de base
        </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            {t('clientForm.fields.firstName')} *
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            onBlur={() => handleInputChange('firstName', formData.firstName.trim())}
            className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.firstName ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder={t('clientForm.placeholders.firstName')}
          />
          {errors.firstName && (
            <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            {t('clientForm.fields.lastName')} (optionnel)
          </label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            onBlur={() => handleInputChange('lastName', formData.lastName.trim())}
            className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.lastName ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder={t('clientForm.placeholders.lastName')}
          />
          {errors.lastName && (
            <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            <Mail className="w-4 h-4 inline mr-1" />
            {t('clientForm.fields.email')} (optionnel)
          </label>
          <div className="relative">
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={() => handleInputChange('email', formData.email.trim())}
              className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={t('clientForm.placeholders.email')}
            />
            {checkingEmail && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            )}
          </div>
          {errors.email && (
            <p className="text-red-600 text-sm mt-1 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-1" />
              {errors.email}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            <Phone className="w-4 h-4 inline mr-1" />
            {t('clientForm.fields.phone')} (optionnel)
          </label>
          <div className="relative">
            <InternationalPhoneInput
              value={phoneState.displayValue}
              country={phoneState.country}
              onChange={(result) => {
                setPhoneState(result);
                setFormData((prev) => ({ ...prev, phone: result.displayValue }));
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.phone;
                  return next;
                });

                if (phoneCheckTimeoutRef.current) clearTimeout(phoneCheckTimeoutRef.current);
                if (result.isValid && result.e164Value) {
                  phoneCheckTimeoutRef.current = setTimeout(() => {
                    checkDuplicate('phone', result.e164Value!);
                  }, 800);
                }
              }}
              selectClassName="w-full px-4 py-3 text-base border rounded-lg border-gray-300"
              inputClassName={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.phone ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={t('clientForm.placeholders.phone')}
              error={errors.phone}
            />
            {checkingPhone && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-textPrimary mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            {t('clientForm.fields.dateOfBirth')} (optionnel)
          </label>
          <input
            type="date"
            value={formData.dateOfBirth}
            disabled={formData.isMinor === true && !formData.dateOfBirth}
            onChange={(e) => {
              const value = e.target.value;
              setFormData((prev) => ({
                ...prev,
                dateOfBirth: value,
                isMinor: value ? prev.isMinor : null,
              }));
            }}
            className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.dateOfBirth ? 'border-red-300' : 'border-gray-300'
            }`}
            max={getMaxDateForInput()}
          />
          {errors.dateOfBirth && (
            <p className="text-red-600 text-sm mt-1">{errors.dateOfBirth}</p>
          )}
          
          {isMinor && (
            <div className="mt-2 bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-orange-800 text-sm flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2" />
                {t('clientForm.minorDetected')}
              </p>
            </div>
          )}
          <label className="mt-3 flex items-center gap-2 text-sm text-textPrimary">
            <input
              type="checkbox"
              checked={isMinor}
              disabled={Boolean(formData.dateOfBirth)}
              onChange={(e) => {
                handleInputChange('isMinor', e.target.checked ? true : null);
              }}
              className="h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            Mineur (optionnel)
          </label>
        </div>
      </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-8">
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-textPrimary mb-4">
          {t('clientForm.steps.address')}
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Adresse de résidence
        </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-textPrimary mb-2">
            <MapPin className="w-4 h-4 inline mr-1" />
            {t('clientForm.fields.street')} (optionnel)
          </label>
          <input
            type="text"
            value={formData.address.street}
            onChange={(e) => handleInputChange('address.street', e.target.value)}
            className={`input-field ${errors['address.street'] ? 'border-red-300 focus:ring-red-500' : ''}`}
            placeholder={t('clientForm.placeholders.street')}
          />
          {errors['address.street'] && (
            <p className="text-red-600 text-sm mt-1">{errors['address.street']}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            {t('clientForm.fields.city')} (optionnel)
          </label>
          <input
            type="text"
            value={formData.address.city}
            onChange={(e) => handleInputChange('address.city', e.target.value)}
            className={`input-field ${errors['address.city'] ? 'border-red-300 focus:ring-red-500' : ''}`}
            placeholder={t('clientForm.placeholders.city')}
          />
          {errors['address.city'] && (
            <p className="text-red-600 text-sm mt-1">{errors['address.city']}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            {t('clientForm.fields.postalCode')} (optionnel)
          </label>
          <input
            type="text"
            value={formData.address.postalCode}
            onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
            className={`input-field ${errors['address.postalCode'] ? 'border-red-300 focus:ring-red-500' : ''}`}
            placeholder={t('clientForm.placeholders.postalCode')}
          />
          {errors['address.postalCode'] && (
            <p className="text-red-600 text-sm mt-1">{errors['address.postalCode']}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-textPrimary mb-2">
            {t('clientForm.fields.country')} (optionnel)
          </label>
          <select
            value={formData.address.country}
            onChange={(e) => handleInputChange('address.country', e.target.value)}
            className={`input-field ${errors['address.country'] ? 'border-red-300 focus:ring-red-500' : ''}`}
          >
            <option value="France">France</option>
            <option value="Belgium">Belgique</option>
            <option value="Switzerland">Suisse</option>
            <option value="Canada">Canada</option>
            <option value="United States">États-Unis</option>
          </select>
          {errors['address.country'] && (
            <p className="text-red-600 text-sm mt-1">{errors['address.country']}</p>
          )}
        </div>
      </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-8">
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-textPrimary mb-4">
          {t('clientForm.steps.identity')}
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Pièce d'identité (optionnel)
        </p>

      <div className="space-y-6">
        {/* Document Upload Section */}
        {renderDocumentSection(formData.identityDocument, false)}

        {/* Manual Entry Section */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-textPrimary mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            {t('clientForm.manualEntry.title')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('clientForm.fields.documentType')}
              </label>
              <select
                value={formData.identityDocument.type}
                onChange={(e) => handleInputChange('identityDocument.type', e.target.value)}
                className="input-field"
              >
                <option value="national_id">{t('clientForm.documentTypes.nationalId')}</option>
                <option value="passport">{t('clientForm.documentTypes.passport')}</option>
                <option value="driving_license">{t('clientForm.documentTypes.drivingLicense')}</option>
                <option value="birth_certificate">{t('clientForm.documentTypes.birthCertificate')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('clientForm.fields.documentNumber')}
              </label>
              <input
                type="text"
                value={formData.identityDocument.number}
                onChange={(e) => handleInputChange('identityDocument.number', e.target.value)}
                className="input-field"
                placeholder={t('clientForm.placeholders.documentNumber')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('clientForm.fields.issueDate')}
              </label>
              <input
                type="date"
                value={formData.identityDocument.issueDate}
                onChange={(e) => handleInputChange('identityDocument.issueDate', e.target.value)}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('clientForm.fields.expiryDate')}
              </label>
              <input
                type="date"
                value={formData.identityDocument.expiryDate}
                onChange={(e) => handleInputChange('identityDocument.expiryDate', e.target.value)}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('clientForm.fields.issuePlace')}
              </label>
              <input
                type="text"
                value={formData.identityDocument.issuePlace}
                onChange={(e) => handleInputChange('identityDocument.issuePlace', e.target.value)}
                className="input-field"
                placeholder={t('clientForm.placeholders.issuePlace')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('clientForm.fields.issuingAuthority')}
              </label>
              <input
                type="text"
                value={formData.identityDocument.issuingAuthority}
                onChange={(e) => handleInputChange('identityDocument.issuingAuthority', e.target.value)}
                className="input-field"
                placeholder={t('clientForm.placeholders.issuingAuthority')}
              />
            </div>
          </div>
        </div>

        {/* Complete Later Acknowledgment - Required Checkbox */}
        <div className={`border rounded-lg p-4 ${
          errors['identityDocAcknowledgment'] ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-200'
        }`}>
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={identityDocLaterAcknowledged}
              onChange={(e) => {
                setIdentityDocLaterAcknowledged(e.target.checked);
                if (errors['identityDocAcknowledgment']) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors['identityDocAcknowledgment'];
                    return newErrors;
                  });
                }
              }}
              className={`h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5 ${
                errors['identityDocAcknowledgment'] ? 'border-red-300' : ''
              }`}
            />
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <Info className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-blue-800">{t('clientForm.completeLater.title')}</h4>
              </div>
              <p className="text-sm text-blue-700 mt-1">{t('clientForm.completeLater.description')}</p>
            </div>
          </label>
          {errors['identityDocAcknowledgment'] && (
            <p className="text-red-600 text-sm mt-2 ml-8 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-1" />
              {errors['identityDocAcknowledgment']}
            </p>
          )}
        </div>
      </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-8">
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-textPrimary mb-4">
          {t('clientForm.steps.guardian')}
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Informations du représentant légal
        </p>

      <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-6 h-6 text-orange-600 mt-1" />
          <div>
            <h3 className="font-medium text-orange-800">{t('clientForm.guardianRequired')}</h3>
            <p className="text-sm text-orange-700 mt-1">{t('clientForm.guardianRequiredDescription')}</p>
          </div>
        </div>
      </div>

      {formData.legalGuardian && (
        <div className="space-y-6">
          {/* Guardian Personal Info */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-textPrimary mb-4">
              Informations personnelles du représentant
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  {t('clientForm.fields.guardianFirstName')} (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.legalGuardian.firstName}
                  onChange={(e) => handleInputChange('legalGuardian.firstName', e.target.value)}
                  onBlur={() => handleInputChange('legalGuardian.firstName', formData.legalGuardian?.firstName?.trim() || '')}
                  className={`input-field ${errors['legalGuardian.firstName'] ? 'border-red-300 focus:ring-red-500' : ''}`}
                  placeholder={t('clientForm.placeholders.guardianFirstName')}
                />
                {errors['legalGuardian.firstName'] && (
                  <p className="text-red-600 text-sm mt-1">{errors['legalGuardian.firstName']}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  {t('clientForm.fields.guardianLastName')} (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.legalGuardian.lastName}
                  onChange={(e) => handleInputChange('legalGuardian.lastName', e.target.value)}
                  onBlur={() => handleInputChange('legalGuardian.lastName', formData.legalGuardian?.lastName?.trim() || '')}
                  className={`input-field ${errors['legalGuardian.lastName'] ? 'border-red-300 focus:ring-red-500' : ''}`}
                  placeholder={t('clientForm.placeholders.guardianLastName')}
                />
                {errors['legalGuardian.lastName'] && (
                  <p className="text-red-600 text-sm mt-1">{errors['legalGuardian.lastName']}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  {t('clientForm.fields.guardianEmail')} (optionnel)
                </label>
                <input
                  type="email"
                  value={formData.legalGuardian.email}
                  onChange={(e) => handleInputChange('legalGuardian.email', e.target.value)}
                  onBlur={() => handleInputChange('legalGuardian.email', formData.legalGuardian?.email?.trim() || '')}
                  className={`input-field ${errors['legalGuardian.email'] ? 'border-red-300 focus:ring-red-500' : ''}`}
                  placeholder={t('clientForm.placeholders.guardianEmail')}
                />
                {errors['legalGuardian.email'] && (
                  <p className="text-red-600 text-sm mt-1">{errors['legalGuardian.email']}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  {t('clientForm.fields.guardianPhone')} (optionnel)
                </label>
                <InternationalPhoneInput
                  value={guardianPhoneState.displayValue}
                  country={guardianPhoneState.country}
                  onChange={(result) => {
                    setGuardianPhoneState(result);
                    setFormData((prev) => ({
                      ...prev,
                      legalGuardian: prev.legalGuardian
                        ? { ...prev.legalGuardian, phone: result.displayValue }
                        : undefined,
                    }));
                    if (result.isValid && errors['legalGuardian.phone']) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next['legalGuardian.phone'];
                        return next;
                      });
                    }
                  }}
                  selectClassName="input-field"
                  inputClassName={`input-field ${errors['legalGuardian.phone'] ? 'border-red-300 focus:ring-red-500' : ''}`}
                  placeholder={t('clientForm.placeholders.guardianPhone')}
                  error={errors['legalGuardian.phone']}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  {t('clientForm.fields.relationship')} (optionnel)
                </label>
                <select
                  value={formData.legalGuardian.relationshipToMinor}
                  onChange={(e) => handleInputChange('legalGuardian.relationshipToMinor', e.target.value)}
                  className="input-field"
                >
                  <option value="parent">{t('clientForm.relationships.parent')}</option>
                  <option value="legal_guardian">{t('clientForm.relationships.legalGuardian')}</option>
                  <option value="other">{t('clientForm.relationships.other')}</option>
                </select>
              </div>

              {formData.legalGuardian.relationshipToMinor === 'other' && (
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    {t('clientForm.fields.relationshipDetails')}
                  </label>
                  <input
                    type="text"
                    value={formData.legalGuardian.relationshipDetails || ''}
                    onChange={(e) => handleInputChange('legalGuardian.relationshipDetails', e.target.value)}
                    className="input-field"
                    placeholder={t('clientForm.placeholders.relationshipDetails')}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Guardian Identity Document */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-textPrimary mb-4">
              {t('clientForm.guardianDocument.title')}
            </h3>
            
            {formData.legalGuardian.identityDocument && (
              <div className="space-y-6">
                {/* Guardian Document Upload Section */}
                {renderDocumentSection(formData.legalGuardian.identityDocument, true)}

                {/* Guardian Document Manual Entry */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('clientForm.fields.documentType')}
                    </label>
                    <select
                      value={formData.legalGuardian.identityDocument.type}
                      onChange={(e) => handleInputChange('legalGuardian.identityDocument.type', e.target.value)}
                      className="input-field"
                    >
                      <option value="national_id">{t('clientForm.documentTypes.nationalId')}</option>
                      <option value="passport">{t('clientForm.documentTypes.passport')}</option>
                      <option value="driving_license">{t('clientForm.documentTypes.drivingLicense')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('clientForm.fields.documentNumber')}
                    </label>
                    <input
                      type="text"
                      value={formData.legalGuardian.identityDocument.number}
                      onChange={(e) => handleInputChange('legalGuardian.identityDocument.number', e.target.value)}
                      className="input-field"
                      placeholder={t('clientForm.placeholders.documentNumber')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('clientForm.fields.issueDate')}
                    </label>
                    <input
                      type="date"
                      value={formData.legalGuardian.identityDocument.issueDate}
                      onChange={(e) => handleInputChange('legalGuardian.identityDocument.issueDate', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('clientForm.fields.expiryDate')}
                    </label>
                    <input
                      type="date"
                      value={formData.legalGuardian.identityDocument.expiryDate || ''}
                      onChange={(e) => handleInputChange('legalGuardian.identityDocument.expiryDate', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('clientForm.fields.issuePlace')}
                    </label>
                    <input
                      type="text"
                      value={formData.legalGuardian.identityDocument.issuePlace}
                      onChange={(e) => handleInputChange('legalGuardian.identityDocument.issuePlace', e.target.value)}
                      className="input-field"
                      placeholder={t('clientForm.placeholders.issuePlace')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t('clientForm.fields.issuingAuthority')}
                    </label>
                    <input
                      type="text"
                      value={formData.legalGuardian.identityDocument.issuingAuthority}
                      onChange={(e) => handleInputChange('legalGuardian.identityDocument.issuingAuthority', e.target.value)}
                      className="input-field"
                      placeholder={t('clientForm.placeholders.issuingAuthority')}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-8">
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-textPrimary mb-4">
          {t('clientForm.steps.consents')}
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Consentements requis
        </p>

      <div className="space-y-6">
        {/* Data Processing Consent (Required) */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={formData.consents.dataProcessing}
              onChange={(e) => handleInputChange('consents.dataProcessing', e.target.checked)}
              className={`h-5 w-5 text-accent-600 focus:ring-accent-500 border-gray-300 rounded mt-0.5 ${
                errors['consents.dataProcessing'] ? 'border-red-300' : ''
              }`}
            />
            <div className="flex-1">
              <div className="font-medium text-textPrimary">{t('clientForm.consents.dataProcessing.title')} (optionnel)</div>
              <p className="text-sm text-textSecondary mt-1">{t('clientForm.consents.dataProcessing.description')}</p>
            </div>
          </label>
          {errors['consents.dataProcessing'] && (
            <p className="text-red-600 text-sm mt-2">{errors['consents.dataProcessing']}</p>
          )}
        </div>

        {/* Photography Consent (Optional) */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={formData.consents.photography}
              onChange={(e) => handleInputChange('consents.photography', e.target.checked)}
              className="h-5 w-5 text-accent-600 focus:ring-accent-500 border-gray-300 rounded mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-textPrimary">{t('clientForm.consents.photography.title')}</div>
              <p className="text-sm text-textSecondary mt-1">{t('clientForm.consents.photography.description')}</p>
            </div>
          </label>
        </div>

        {/* Marketing Consent (Optional) */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={formData.consents.marketing}
              onChange={(e) => handleInputChange('consents.marketing', e.target.checked)}
              className="h-5 w-5 text-accent-600 focus:ring-accent-500 border-gray-300 rounded mt-0.5"
            />
            <div className="flex-1">
              <div className="font-medium text-textPrimary">{t('clientForm.consents.marketing.title')}</div>
              <p className="text-sm text-textSecondary mt-1">{t('clientForm.consents.marketing.description')}</p>
            </div>
          </label>
        </div>

        {/* Minor-specific consents */}
        {isMinor && (
          <>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={formData.consents.minorConsent}
                  onChange={(e) => handleInputChange('consents.minorConsent', e.target.checked)}
                  className={`h-5 w-5 text-accent-600 focus:ring-accent-500 border-gray-300 rounded mt-0.5 ${
                    errors['consents.minorConsent'] ? 'border-red-300' : ''
                  }`}
                />
                <div className="flex-1">
                  <div className="font-medium text-textPrimary">{t('clientForm.consents.minorConsent.title')} (optionnel)</div>
                  <p className="text-sm text-textSecondary mt-1">{t('clientForm.consents.minorConsent.description')}</p>
                </div>
              </label>
              {errors['consents.minorConsent'] && (
                <p className="text-red-600 text-sm mt-2">{errors['consents.minorConsent']}</p>
              )}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={formData.consents.guardianPresence}
                  onChange={(e) => handleInputChange('consents.guardianPresence', e.target.checked)}
                  className={`h-5 w-5 text-accent-600 focus:ring-accent-500 border-gray-300 rounded mt-0.5 ${
                    errors['consents.guardianPresence'] ? 'border-red-300' : ''
                  }`}
                />
                <div className="flex-1">
                  <div className="font-medium text-textPrimary">{t('clientForm.consents.guardianPresence.title')} (optionnel)</div>
                  <p className="text-sm text-textSecondary mt-1">{t('clientForm.consents.guardianPresence.description')}</p>
                </div>
              </label>
              {errors['consents.guardianPresence'] && (
                <p className="text-red-600 text-sm mt-2">{errors['consents.guardianPresence']}</p>
              )}
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );

  const handleCreateAnother = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      isMinor: null,
      address: {
        street: '',
        city: '',
        postalCode: '',
        country: 'France'
      },
      identityDocument: {
        type: 'national_id',
        number: '',
        issueDate: '',
        expiryDate: '',
        issuePlace: '',
        issuingAuthority: ''
      },
      legalGuardian: undefined,
      consents: {
        dataProcessing: false,
        photography: false,
        marketing: false,
        minorConsent: false,
        guardianPresence: false
      }
    });
    setPhoneState(getPhoneState(''));
    setGuardianPhoneState(getPhoneState(''));
    setCurrentStep(1);
    setShowSuccess(false);
    setSuccessData(null);
    setErrors({});
    setIdentityDocLaterAcknowledged(false);
  };

  const handleBackToDashboard = () => {
    if (isBackoffice) {
      window.location.href = '/dashboard';
    } else {
      window.location.href = '/';
    }
  };

  if (showSuccess && successData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-display font-bold text-textPrimary mb-4">
            {t('clientForm.success.title')}
          </h2>
          
          <p className="text-textSecondary mb-8">
            {t('clientForm.success.message', { 
              name: `${successData.data?.client.firstName} ${successData.data?.client.lastName}` 
            })}
          </p>

          {/* Client Information */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-textPrimary mb-4 flex items-center justify-center">
              <User className="w-5 h-5 mr-2" />
              Informations Client
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div>
                <span className="text-sm text-gray-500">Nom complet</span>
                <p className="font-medium">{successData.data?.client.firstName} {successData.data?.client.lastName}</p>
              </div>
              
              <div>
                <span className="text-sm text-gray-500">Email</span>
                <p className="font-medium">{successData.data?.client.email}</p>
              </div>
              
              <div>
                <span className="text-sm text-gray-500">Téléphone</span>
                <p className="font-medium">{successData.data?.client.phone}</p>
              </div>
              
              {successData.data?.client.isMinor !== null
                && successData.data?.client.isMinor !== undefined && (
                  <div>
                    <span className="text-sm text-gray-500">Statut</span>
                    <p className="font-medium">
                      {successData.data.client.isMinor ? 'Mineur' : 'Majeur'}
                    </p>
                  </div>
                )}
              
              <div className="md:col-span-2">
                <span className="text-sm text-gray-500">ID Client</span>
                <p className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{successData.data?.client.id}</p>
              </div>
            </div>
          </div>

          {/* Legal Guardian Information */}
          {successData.data?.legalGuardian && (
            <div className="bg-yellow-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-textPrimary mb-4 flex items-center justify-center">
                <Shield className="w-5 h-5 mr-2" />
                {t('clientForm.success.guardianInfo')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <div>
                  <span className="text-sm text-gray-500">Nom complet</span>
                  <p className="font-medium">{successData.data.legalGuardian.firstName} {successData.data.legalGuardian.lastName}</p>
                </div>
                
                <div>
                  <span className="text-sm text-gray-500">Email</span>
                  <p className="font-medium">{successData.data.legalGuardian.email}</p>
                </div>
                
                <div>
                  <span className="text-sm text-gray-500">Téléphone</span>
                  <p className="font-medium">{successData.data.legalGuardian.phone}</p>
                </div>
                
                <div>
                  <span className="text-sm text-gray-500">ID Représentant</span>
                  <p className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{successData.data.legalGuardian.id}</p>
                </div>
              </div>
              
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">{t('clientForm.success.guardianNotified')}</p>
              </div>
            </div>
          )}

          {/* QR Code */}
          {successData.data?.qrCode && (
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-textPrimary mb-4 flex items-center justify-center">
                <QrCode className="w-5 h-5 mr-2" />
                {t('clientForm.success.qrCode')}
              </h3>
              
              <div className="flex justify-center mb-4">
                <img 
                  src={successData.data.qrCode.dataUrl} 
                  alt="Client QR Code"
                  className="w-32 h-32 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleCreateAnother}
              className="btn-secondary"
            >
              {t('clientForm.success.createAnother')}
            </button>
            <button
              onClick={handleBackToDashboard}
              className="btn-primary"
            >
              {isBackoffice ? t('clientForm.success.backToDashboard') : t('clientForm.success.backToHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Progress Bar */}
      {renderProgressBar()}

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-xl p-8">
        {/* General Error */}
        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-red-800 text-sm">{errors.general}</span>
            </div>
          </div>
        )}

        {/* Step Content */}
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && requireLegalGuardian && isMinor && renderStep4()}
        {currentStep === 5 && renderStep5()}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8">
          {currentStep > 1 ? (
            <button
              onClick={handlePrevious}
              className="btn-secondary flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('common.previous')}</span>
            </button>
          ) : (
            <div></div>
          )}

          <button
            onClick={currentStep === 5 ? handleSubmit : handleNext}
            disabled={loading}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('clientForm.creating')}</span>
              </>
            ) : (
              <>
                <span>{currentStep === 5 ? t('clientForm.createAccount') : t('common.next')}</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
